import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Plus, X, Trash2, Undo2, ClipboardList } from "lucide-react";
import {
  usePurchases,
  useCreatePurchase,
  useReceivePurchase,
  useVoidPurchase,
  usePurchaseReturns,
  useCreatePurchaseReturn,
  PURCHASE_STATUS_LABEL,
  PURCHASE_RETURN_REASON_LABEL,
  PurchaseStatus,
  PurchaseRecord,
  PurchaseReturnReason,
} from "@/api/purchases";
import { useSuppliers, useProducts, useBranches } from "@/api/inventory";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const STATUS_STYLE: Record<PurchaseStatus, string> = {
  BORRADOR: "bg-gray-100 text-gray-600",
  PARCIAL: "bg-amber-100 text-amber-700",
  RECIBIDA: "bg-green-100 text-green-700",
  ANULADA: "bg-red-100 text-red-700",
};

/** Compras a proveedor: registrar una compra (BORRADOR) no toca el inventario; al recibirla, se suma el stock de cada línea. */
export default function PurchasesPage() {
  const [status, setStatus] = useState<PurchaseStatus | "">("");
  const [branchFilter, setBranchFilter] = useState<string>("");
  const [showCreate, setShowCreate] = useState(false);

  const { data: branches } = useBranches();
  const { data, isLoading } = usePurchases({ status: status || undefined, branchId: branchFilter || undefined, pageSize: 50 });
  const { data: draftData } = usePurchases({ status: "BORRADOR", pageSize: 1 });
  const { data: partialData } = usePurchases({ status: "PARCIAL", pageSize: 1 });
  const voidPurchase = useVoidPurchase();
  const purchases = data?.data ?? [];
  const pendingCount = (draftData?.meta?.total ?? 0) + (partialData?.meta?.total ?? 0);
  const [receiving, setReceiving] = useState<PurchaseRecord | null>(null);
  const [returning, setReturning] = useState<PurchaseRecord | null>(null);
  const [showReturns, setShowReturns] = useState(false);

  const handleVoid = async (id: string) => {
    if (!window.confirm("¿Anular esta compra?")) return;
    try {
      await voidPurchase.mutateAsync(id);
      toast.success("Compra anulada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Compras y órdenes de compra</h1>
          <p className="text-sm text-gray-500">
            Crea una orden de compra al proveedor (queda pendiente, sin tocar inventario) y recíbela cuando llegue la mercancía para actualizar el stock.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/store/purchase-quotes" className="btn-secondary">
            <ClipboardList size={16} className="mr-1.5" /> Cotizaciones de compra
          </Link>
          <button type="button" className="btn-secondary" onClick={() => setShowReturns((v) => !v)}>
            <Undo2 size={16} className="mr-1.5" /> {showReturns ? "Ver compras" : "Devoluciones a proveedor"}
          </button>
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nueva orden de compra
          </button>
        </div>
      </div>

      {showReturns && <PurchaseReturnsHistory />}

      {!showReturns && pendingCount > 0 && (
        <div className="card flex items-center justify-between border-l-4 border-amber-400 bg-amber-50 p-4">
          <p className="text-sm text-amber-800">
            <strong>{pendingCount}</strong> orden{pendingCount === 1 ? "" : "es"} de compra pendiente{pendingCount === 1 ? "" : "s"} de recibir (total o parcialmente).
          </p>
          <button type="button" className="text-xs font-medium text-amber-800 underline hover:text-amber-900" onClick={() => setStatus("")}>
            Ver pendientes
          </button>
        </div>
      )}

      {!showReturns && (
      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as PurchaseStatus | "")}>
          <option value="">Todos los estados</option>
          {Object.entries(PURCHASE_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select className="input-field w-auto" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
          <option value="">Todas las sucursales</option>
          {branches?.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      )}

      {!showReturns && (
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Sucursal</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Proveedor</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Factura</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && purchases.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No hay compras registradas.
                </td>
              </tr>
            )}
            {purchases.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-600">{p.purchaseDate}</td>
                <td className="px-4 py-3 text-gray-600">{p.branch?.name ?? "—"}</td>
                <td className="px-4 py-3 text-gray-900">{p.supplier?.name}</td>
                <td className="px-4 py-3 text-gray-600">{p.invoiceNumber ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[p.status]}`}>{PURCHASE_STATUS_LABEL[p.status]}</span>
                </td>
                <td className="px-4 py-3 text-right text-gray-900">{currency.format(Number(p.total))}</td>
                <td className="px-4 py-3 text-right">
                  {(p.status === "BORRADOR" || p.status === "PARCIAL") && (
                    <button type="button" className="mr-3 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setReceiving(p)}>
                      {p.status === "PARCIAL" ? "Recibir resto" : "Recibir"}
                    </button>
                  )}
                  {(p.status === "PARCIAL" || p.status === "RECIBIDA") &&
                    p.lines.some((l) => Number(l.receivedQuantity) - Number(l.returnedQuantity) > 0.0005) && (
                      <button type="button" className="mr-3 text-xs font-medium text-amber-700 hover:text-amber-900" onClick={() => setReturning(p)}>
                        Devolver
                      </button>
                    )}
                  {(p.status === "BORRADOR" || p.status === "PARCIAL" || p.status === "RECIBIDA") && (
                    <button type="button" className="text-xs font-medium text-red-600 hover:text-red-800" onClick={() => handleVoid(p.id)}>
                      Anular
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {showCreate && <CreatePurchaseModal onClose={() => setShowCreate(false)} />}
      {receiving && <ReceivePurchaseModal purchase={receiving} onClose={() => setReceiving(null)} />}
      {returning && <ReturnPurchaseModal purchase={returning} onClose={() => setReturning(null)} />}
    </div>
  );
}

function ReceivePurchaseModal({ purchase, onClose }: { purchase: PurchaseRecord; onClose: () => void }) {
  const receivePurchase = useReceivePurchase();
  const pendingLines = purchase.lines
    .map((l) => ({ ...l, pending: Number(l.quantity) - Number(l.receivedQuantity) }))
    .filter((l) => l.pending > 0.0005);

  // Por defecto se propone recibir TODO lo pendiente de cada línea (el caso más común: llegó todo el pedido);
  // el usuario ajusta a mano si el proveedor mandó menos.
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(pendingLines.map((l) => [l.id, String(l.pending)]))
  );

  const setAmount = (lineId: string, value: string) => setAmounts((prev) => ({ ...prev, [lineId]: value }));

  const linesToSend = pendingLines
    .map((l) => ({ purchaseLineId: l.id, quantity: Number(amounts[l.id] ?? 0) }))
    .filter((l) => l.quantity > 0);
  const isValid = linesToSend.length > 0 && pendingLines.every((l) => {
    const qty = Number(amounts[l.id] ?? 0);
    return qty >= 0 && qty <= l.pending + 0.0005;
  });

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Revisa las cantidades: no pueden ser negativas ni superar lo pendiente.");
      return;
    }
    try {
      const result = await receivePurchase.mutateAsync({ id: purchase.id, lines: linesToSend });
      toast.success(result.status === "PARCIAL" ? "Recepción parcial registrada — inventario actualizado." : "Compra recibida — inventario actualizado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Recibir mercancía — {purchase.supplier?.name}</h2>
            <p className="text-xs text-gray-500">Ajusta la cantidad si el proveedor entregó menos de lo pedido; el resto queda pendiente para recibirlo después.</p>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          {pendingLines.length === 0 && <p className="text-sm text-gray-400">No queda nada pendiente por recibir en esta compra.</p>}
          {pendingLines.map((l) => (
            <div key={l.id} className="grid grid-cols-12 items-center gap-2 rounded-md border border-gray-100 p-2">
              <div className="col-span-6">
                <p className="text-sm text-gray-900">{l.product?.name ?? l.productId}</p>
                <p className="text-xs text-gray-400">
                  {l.product?.sku} · pendiente {l.pending} {l.product?.unit?.toLowerCase()}
                  {Number(l.receivedQuantity) > 0 ? ` (ya recibido: ${l.receivedQuantity})` : ""}
                </p>
              </div>
              <div className="col-span-3 text-xs text-gray-500">de {l.quantity} pedidas</div>
              <div className="col-span-3">
                <input
                  className="input-field"
                  type="number"
                  min={0}
                  max={l.pending}
                  step="0.001"
                  value={amounts[l.id] ?? ""}
                  onChange={(e) => setAmount(l.id, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || receivePurchase.isPending} onClick={handleSubmit}>
            {receivePurchase.isPending ? "Guardando..." : "Confirmar recepción"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DraftLine {
  productId: string;
  quantity: string;
  unitCost: string;
}

function CreatePurchaseModal({ onClose }: { onClose: () => void }) {
  const { data: suppliers } = useSuppliers(true);
  const { data: branches } = useBranches(true);
  const { data: productsResponse } = useProducts({ pageSize: 200, isActive: true });
  const products = productsResponse?.data ?? [];
  const createPurchase = useCreatePurchase();

  const [branchId, setBranchId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ productId: "", quantity: "1", unitCost: "0" }]);

  useEffect(() => {
    if (!branchId && branches && branches.length > 0) {
      setBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, branchId]);

  const addLine = () => setLines((prev) => [...prev, { productId: "", quantity: "1", unitCost: "0" }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const total = lines.reduce((sum, l) => sum + Number(l.quantity || 0) * Number(l.unitCost || 0), 0);
  const isValid = branchId && supplierId && purchaseDate && lines.every((l) => l.productId && Number(l.quantity) > 0);

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Elige sucursal, proveedor y completa cada línea con producto y cantidad.");
      return;
    }
    try {
      await createPurchase.mutateAsync({
        branchId,
        supplierId,
        purchaseDate,
        invoiceNumber: invoiceNumber || undefined,
        lines: lines.map((l) => ({ productId: l.productId, quantity: l.quantity, unitCost: l.unitCost })),
      });
      toast.success("Orden de compra creada — queda pendiente hasta recibirla.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nueva orden de compra</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Sucursal</label>
              <select className="input-field mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Seleccione...</option>
                {branches?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Proveedor</label>
              <select className="input-field mt-1" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">Seleccione...</option>
                {suppliers?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha</label>
              <input className="input-field mt-1" type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700"># Factura del proveedor (opcional)</label>
            <input className="input-field mt-1" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Productos</p>
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={addLine}>
                + Agregar línea
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <select
                    className="input-field col-span-5"
                    value={line.productId}
                    onChange={(e) => updateLine(index, { productId: e.target.value })}
                  >
                    <option value="">Producto...</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input-field col-span-3"
                    type="number"
                    placeholder="Cantidad"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  />
                  <input
                    className="input-field col-span-3"
                    type="number"
                    placeholder="Costo unitario"
                    value={line.unitCost}
                    onChange={(e) => updateLine(index, { unitCost: e.target.value })}
                  />
                  <button type="button" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeLine(index)} aria-label="Quitar línea">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end text-sm font-semibold text-gray-900">Subtotal: {currency.format(total)}</div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createPurchase.isPending} onClick={handleSubmit}>
            {createPurchase.isPending ? "Guardando..." : "Crear orden (pendiente)"}
          </button>
        </div>
      </div>
    </div>
  );
}

const RETURN_REASON_OPTIONS = Object.entries(PURCHASE_RETURN_REASON_LABEL) as Array<[PurchaseReturnReason, string]>;

/** Devuelve al proveedor mercancía ya recibida (dañada, vencida, no pedida, defectuosa u otro motivo) — descuenta inventario y no puede exceder lo recibido y aún no devuelto de cada línea. */
function ReturnPurchaseModal({ purchase, onClose }: { purchase: PurchaseRecord; onClose: () => void }) {
  const createReturn = useCreatePurchaseReturn();
  const returnableLines = purchase.lines
    .map((l) => ({ ...l, returnable: Number(l.receivedQuantity) - Number(l.returnedQuantity) }))
    .filter((l) => l.returnable > 0.0005);

  const [reason, setReason] = useState<PurchaseReturnReason>("DANADO");
  const [notes, setNotes] = useState("");
  const [amounts, setAmounts] = useState<Record<string, string>>(() => Object.fromEntries(returnableLines.map((l) => [l.id, "0"])));

  const setAmount = (lineId: string, value: string) => setAmounts((prev) => ({ ...prev, [lineId]: value }));

  const linesToSend = returnableLines
    .map((l) => ({ purchaseLineId: l.id, quantity: Number(amounts[l.id] ?? 0) }))
    .filter((l) => l.quantity > 0);
  const isValid =
    linesToSend.length > 0 &&
    returnableLines.every((l) => {
      const qty = Number(amounts[l.id] ?? 0);
      return qty >= 0 && qty <= l.returnable + 0.0005;
    });

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Revisa las cantidades: no pueden ser negativas ni superar lo recibido y no devuelto.");
      return;
    }
    try {
      await createReturn.mutateAsync({ purchaseId: purchase.id, reason, notes: notes || undefined, lines: linesToSend });
      toast.success("Devolución a proveedor registrada — inventario actualizado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Devolver mercancía al proveedor — {purchase.supplier?.name}</h2>
            <p className="text-xs text-gray-500">Solo puedes devolver lo que ya se recibió y aún no se ha devuelto de esta compra.</p>
          </div>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Motivo de la devolución</label>
            <select className="input-field mt-1" value={reason} onChange={(e) => setReason(e.target.value as PurchaseReturnReason)}>
              {RETURN_REASON_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {returnableLines.length === 0 && <p className="text-sm text-gray-400">No hay mercancía recibida disponible para devolver en esta compra.</p>}
          {returnableLines.map((l) => (
            <div key={l.id} className="grid grid-cols-12 items-center gap-2 rounded-md border border-gray-100 p-2">
              <div className="col-span-6">
                <p className="text-sm text-gray-900">{l.product?.name ?? l.productId}</p>
                <p className="text-xs text-gray-400">
                  {l.product?.sku} · disponible para devolver: {l.returnable} {l.product?.unit?.toLowerCase()}
                </p>
              </div>
              <div className="col-span-3 text-xs text-gray-500">recibido {l.receivedQuantity}</div>
              <div className="col-span-3">
                <input
                  className="input-field"
                  type="number"
                  min={0}
                  max={l.returnable}
                  step="0.001"
                  value={amounts[l.id] ?? ""}
                  onChange={(e) => setAmount(l.id, e.target.value)}
                />
              </div>
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createReturn.isPending} onClick={handleSubmit}>
            {createReturn.isPending ? "Guardando..." : "Registrar devolución"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Historial de devoluciones a proveedor de todas las compras — reemplaza la tabla de compras mientras está activo. */
function PurchaseReturnsHistory() {
  const { data, isLoading } = usePurchaseReturns({ pageSize: 50 });
  const returns = data?.data ?? [];

  return (
    <div className="card overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Sucursal</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Proveedor</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Motivo</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Productos</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Registrada por</th>
            <th className="px-4 py-3 text-right font-medium text-gray-500">Valor</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                Cargando...
              </td>
            </tr>
          )}
          {!isLoading && returns.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                No hay devoluciones a proveedor registradas.
              </td>
            </tr>
          )}
          {returns.map((r) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-600">{new Date(r.createdAt).toLocaleString("es-CO")}</td>
              <td className="px-4 py-3 text-gray-600">{r.branch?.name ?? "—"}</td>
              <td className="px-4 py-3 text-gray-900">{r.supplier?.name}</td>
              <td className="px-4 py-3 text-gray-600">{PURCHASE_RETURN_REASON_LABEL[r.reason]}</td>
              <td className="px-4 py-3 text-gray-600">
                {r.lines.map((l) => `${l.product?.name ?? l.productId} (${l.quantity})`).join(", ")}
              </td>
              <td className="px-4 py-3 text-gray-600">{r.createdBy?.fullName ?? "—"}</td>
              <td className="px-4 py-3 text-right text-gray-900">{currency.format(Number(r.totalValue))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
